import React, { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';

import { useDispatch } from "react-redux";
import moment from "moment";
import { Col, Row } from "react-bootstrap";
import Thumbnail from "src/components/thumbnail";

import { fetchTeamsLearningsReport } from "src/redux/actions/coaching/reports";
import InfiniteScroll from "src/components/infiniteScroll";
import { Link } from "react-router-dom";
import Actions from "src/components/actionbar";
import DownloadExcel from "src/components/downloadExcel";
import { CourseReportContext } from "../../../context";
import Text from "src/components/text";

import cx from "classnames";
import _ from "lodash";
import styles from "./learnings.module.scss";

const defaultAssetImage = "/images/defaultIcons/default_asset.jpg";

const LEARNINGS_REPORT_SCHEMA = [
  { column: "Name", type: String, value: (row) => row.name },
  { column: "Type", type: String, value: (row) => row.type },
  { column: "Unique Learners Enrolled", type: Number, value: (row) => row.enrolledLearners },
  { column: "Learnings Started", type: Number, value: (row) => row.started },
  { column: "Learnings Completed", type: Number, value: (row) => row.completed },
  { 
    column: "Pass %", 
    type: Number, 
    value: (row) => row.passPercentage 
  },
  { column: "Avg. Score", type: Number, value: (row) => row.averageScore },
];

const LearningsReport: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [query, setQuery] = useState<string | null>("");
  const [sortPreference, setSortPreference] = useState<{
    sortBy: string;
    orderBy: string;
  }>({ sortBy: "started", orderBy: "DESC" });

  const reportContext = useContext(CourseReportContext);

  const {
    loading,
    loaded,
    learnings,
    currentPage,
    nextPage,
    hasMoreItems,
    pagination,
  } = useSelector((state: any) => {
    const learningsState = _.get(state.coaching, "reports.teams.learnings");
    const loading = _.get(learningsState, "loading");
    const loaded = _.get(learningsState, "loaded");
    const pagination = _.get(learningsState, "pagination");
    const currentPage = _.get(pagination, "currentPage", 0);
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");

    const learnings = _.get(learningsState, "items") || [];

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      loading: loading,
      loaded: loaded,
      learnings: learnings,
      currentPage: currentPage,
      nextPage: nextPage,
      hasMoreItems: hasMoreItems,
      pagination: pagination,
    };
  }, _.isEqual);

  const fetchState = {
    loading: loading,
    succeeded: loaded,
    error: false,
  };

  const fetchLearningsForPage = (query, page) => {
    const request = {
      query: query,
      page: page,
      limit: 10,
      sort: sortPreference,
      startDate: reportContext.startDate,
      endDate: reportContext.endDate
    };

    dispatch(fetchTeamsLearningsReport(request));
  };

  const handleLoadMore = () => {
    if (nextPage) {
      fetchLearningsForPage(query, nextPage);
    }
  };

  const handleSort = (sortBy: string, orderBy: string) => {
    setSortPreference({ sortBy, orderBy });
  };

  const renderSort = (orderA: string, orderB: string, sort: string) => {
    return (
      <span
        className={cx(styles.sort, {
          [styles.hideSort]: sortPreference.sortBy !== sort,
        })}
      >
        <i
          className={cx("fa-solid fa-angle-up", {
            [styles.active]:
              sortPreference.orderBy === orderA &&
              sortPreference.sortBy === sort,
          })}
          onClick={() => handleSort(sort, orderA)}
        ></i>
        <i
          className={cx("fa-solid fa-angle-down", {
            [styles.active]:
              sortPreference.orderBy === orderB &&
              sortPreference.sortBy === sort,
          })}
          onClick={() => handleSort(sort, orderB)}
        ></i>
      </span>
    );
  };

  const formatDataForDownload = (data) => {
    if (_.isEmpty(data)) {
      return [];
    }

    return data.map((learning) => ({
      name: learning?.course?.name ?? "N/A",
      type: learning.type ?? "N/A",
      enrolledLearners: learning.enrolled ?? 0,
      started: learning.started,
      completed: learning.completed,
      passPercentage: Math.min(
        Math.round(learning?.passPercentage || 0),
        100
      ),
      averageScore: Math.min(
        Math.round((learning?.averageScore || 0) * 100),
        100
      ),}));
      };

  const handleQueryChange = (query: any) => {
    setQuery(query || "");
  };

  const handleClick = (item) => {
    switch (item.type) {
      case "COURSE":
        navigate(`/coaching/manage/courses/draft/${item.course.id}`);
        break;
      case "SIMULATION":
        navigate(`/coaching/manage/simulations/draft/${item.course.id}`);
        break;
      case "PODCAST":
        navigate(`/coaching/manage/podcasts/draft/${item.course.id}`);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    fetchLearningsForPage(query, 0);
  }, [query, sortPreference, reportContext.startDate, reportContext.endDate]);

  return (
    <section className={styles.container}>
      <div className={styles.header}>
        <div className={styles.heading}>
          <h3>course insights: participation, progress & results</h3>
          <span className={styles.description}>
            measure learning impact with every course
          </span>
        </div>
        <div className={styles.actions}>
          <Actions.Search query={query} onSearch={handleQueryChange} />
          {_.size(learnings) > 0 && (
            <div className={styles.downloadButton}>
              <DownloadExcel
                title="Course Insights Report"
                fileName="CourseInsightsReport"
                schema={LEARNINGS_REPORT_SCHEMA}
                data={formatDataForDownload(learnings)}
                fetchState={fetchState}
                pagination={pagination}
                fetchDataForPage={(page) => fetchLearningsForPage("", page + 1)}
                invalidateData={() => {}}
              />
            </div>
          )}
        </div>
      </div>

      <div className={cx(styles.learningTable)}>
        {!loading && _.size(learnings) > 0 && (
          <Row className={styles.tableHeader}>
            <Col lg={3} className={styles.heading}>
              courses
            </Col>
            <Col lg={2} className={styles.heading}>
              learners enrolled
              {renderSort("ASC", "DESC", "enrolled")}
            </Col>
            <Col lg={2} className={styles.heading}>
              learning attempts
              {renderSort("ASC", "DESC", "started")}
            </Col>
            <Col lg={2} className={styles.heading}>
              completions
              {renderSort("ASC", "DESC", "completed")}
            </Col>
            <Col lg={2} className={styles.heading}>
              pass %
              {renderSort("ASC", "DESC", "passPercentage")}
            </Col>
            <Col lg={1} className={styles.heading}>
              avg. score
              {renderSort("ASC", "DESC", "averageScore")}
            </Col>
          </Row>
        )}

        <InfiniteScroll
          className={styles.learnersList}
          loading={loading}
          hasMore={hasMoreItems}
          loadMore={handleLoadMore}
          loader={() => {
            return _.times(
              _.size(learnings) === 0 || currentPage == 0 ? 4 : 1,
              (index) => <PlaceholderLearning key={index} />,
            );
          }}
        >
          {_.size(learnings) === 0 && !loading && (
            <span className={styles.noRecordsFound}>No results found</span>
          )}

          {_.size(learnings) > 0 &&
            learnings.map((item, index) => {
              const createdDate = moment(item.course.createdDate).format(
                "DD MMM YYYY",
              );
              return (
                <Row key={index} className={styles.tableRow} onClick={() => handleClick(item)}>
                  <Col lg={3} className={styles.info}>
                    <Thumbnail
                      media={item.course.banner?.background}
                      defaultImage={defaultAssetImage}
                      className={styles.thumbnail}
                    />
                    <div className={styles.details}>
                      <span className={styles.title}>
                        <Text className={styles.name} text={item?.course?.name || ""} lines={1} />
                      </span>
                      <span className={styles.type}>
                        {_.capitalize(item.type)}
                      </span>
                    </div>
                  </Col>
                  <Col lg={2} className={styles.assignedUsers}>
                    {item.enrolled}
                  </Col>
                  <Col lg={2} className={styles.assignedUsers}>
                    {item.started}
                  </Col>
                  <Col lg={2} className={styles.assignedUsers}>
                    {item.completed}
                  </Col>
                  <Col
                      lg={2}
                      className={cx(styles.columnHeading, styles.assignedUsers)}
                    >
                   {Math.min(Math.round(item?.passPercentage || 0), 100)}%
                    </Col>
                  <Col lg={1} className={styles.avgScore}>
                  {Math.min(Math.round((item?.averageScore || 0) * 100), 100)}%
                  </Col>
                </Row>
              );
            })}
        </InfiniteScroll>
      </div>
    </section>
  );
};

export default LearningsReport;

const PlaceholderLearning = () => {
  return (
    <>
      <Row className={cx(styles.tableRow, styles.placeholder)}>
        <Col lg={5} className={styles.info}>
          <div className={cx(styles.thumbnail, styles.block)}></div>
          <div className={styles.details}>
            <h4 className={cx(styles.title, styles.block)}></h4>
            <span className={cx(styles.type, styles.block)}></span>
          </div>
        </Col>
        <Col lg={2} className={styles.assignedUsers}>
          <div className={styles.block}></div>
        </Col>
        <Col lg={2} className={styles.progress}>
          <div className={styles.block}></div>
        </Col>
        <Col lg={2} className={styles.createdDate}>
          <div className={styles.block}></div>
        </Col>
        <Col lg={1} className={styles.avgScore}>
          <div className={styles.block}></div>
        </Col>
      </Row>
    </>
  );
};
